import React from 'react';
import { Button, Result } from 'antd';

/**
 * 全局错误边界（2026-10-14）
 * 页面渲染抛错时不再"整页白屏"，改为展示可恢复的错误提示 + 错误详情，
 * 并把错误上报后端 /api/logs/frontend-error 便于排查。
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    const msg = String((error && error.message) || error || '');
    // ⚠️ 2026-10-19 重修：chunk 加载失败自动重载
    //   旧版问题：window.location.reload() 拿到的仍是【被缓存的旧 index.html】
    //            → 重载无效，用户卡死在报错页
    //   新版：带 _v 时间戳参数强制绕过缓存；冷却 15s → 3s；本次会话只自动重载一次
    const isChunkError = /Loading chunk [\w.-]+ failed/i.test(msg)
      || /Loading CSS chunk [\w.-]+ failed/i.test(msg)
      || /ChunkLoadError/i.test(msg)
      || /Failed to fetch dynamically imported module/i.test(msg)
      || /Importing a module script failed/i.test(msg);
    if (isChunkError) {
      try {
        const AT_KEY = 'chunk_reload_at';
        const DONE_KEY = 'chunk_reload_done';
        const last = Number(sessionStorage.getItem(AT_KEY) || 0);
        const already = sessionStorage.getItem(DONE_KEY) === '1';
        if (!already && Date.now() - last > 3000) {
          sessionStorage.setItem(AT_KEY, String(Date.now()));
          sessionStorage.setItem(DONE_KEY, '1');
          // eslint-disable-next-line no-console
          console.warn('[ErrorBoundary] chunk 加载失败，自动重载（绕过缓存）：', msg);
          try {
            const u = new URL(window.location.href);
            u.searchParams.set('_v', String(Date.now()));
            window.location.replace(u.toString());
          } catch (e) {
            window.location.reload();
          }
          return;
        }
      } catch (e) { /* sessionStorage 不可用则继续走正常上报 */ }
    }
    // 上报后端（失败不影响页面）
    try {
      const payload = {
        message: String((error && error.message) || error),
        stack: String((error && error.stack) || ''),
        component_stack: String((info && info.componentStack) || ''),
        url: window.location.href,
        ua: navigator.userAgent,
        time: new Date().toISOString(),
      };
      fetch('/api/account-logs/frontend-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
      // 同时打到控制台，方便现场看
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', payload);
    } catch (e) { /* ignore */ }
  }

  handleReload = () => {
    // 带时间戳绕过 index.html 缓存
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('_v', String(Date.now()));
      window.location.replace(u.toString());
    } catch (e) {
      window.location.reload();
    }
  };

  handleBack = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.error) {
      const msg = String((this.state.error && this.state.error.message) || this.state.error || '');
      const isChunkError = /Loading chunk [\w.-]+ failed/i.test(msg) || /ChunkLoadError/i.test(msg)
        || /Failed to fetch dynamically imported module/i.test(msg);
      return (
        <Result
          status="warning"
          title={isChunkError ? '页面已更新，正在重新加载' : '页面出错了'}
          subTitle={
            <span>
              {isChunkError
                ? '系统刚发布新版本，页面正在自动刷新。若长时间未跳转，请点「重新加载」。'
                : '页面渲染时发生异常，数据不会丢失。请点击「重新加载」；若反复出现，请把下面这行信息发给管理员。'}
              <br />
              <code style={{ color: '#c0392b', wordBreak: 'break-all' }}>{msg}</code>
            </span>
          }
          extra={[
            <Button type="primary" key="reload" onClick={this.handleReload}>重新加载</Button>,
            <Button key="back" onClick={this.handleBack}>返回首页</Button>,
          ]}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
